import { avatarBackground, markBackground } from "@/lib/art";
import { cn } from "@/lib/cn";
import { firstLetter, initials } from "@/lib/format";

interface AvatarProps {
  name: string;
  src?: string | null;
  /** Seeds the placeholder gradient; defaults to the name. */
  seed?: string;
  size?: number;
  /** "brand" renders a logo-style mark, "letter" a plain grey initial (search "Other"). */
  variant?: "person" | "brand" | "letter";
  className?: string;
}

export function Avatar({ name, src, seed, size = 36, variant = "person", className }: AvatarProps) {
  const box = { width: size, height: size };
  const base = cn("relative inline-grid shrink-0 place-items-center overflow-hidden rounded-full select-none", className);

  if (src) {
    return (
      // eslint-disable-next-line @next/next/no-img-element -- CDN thumbnails come straight from the content API
      <img src={src} alt="" width={size} height={size} loading="lazy" className={cn(base, "bg-surface object-cover")} style={box} />
    );
  }

  if (variant === "letter") {
    return (
      <span aria-hidden className={cn(base, "bg-white/10 text-soft")} style={{ ...box, fontSize: size * 0.32 }}>
        {firstLetter(name)}
      </span>
    );
  }

  if (variant === "brand") {
    return (
      <span aria-hidden className={cn(base, "bg-[#0b0b10] ring-1 ring-white/10")} style={box}>
        <span
          className="bg-clip-text font-black tracking-tight text-transparent"
          style={{ backgroundImage: markBackground(seed ?? name), fontSize: size * 0.38, lineHeight: 1 }}
        >
          {initials(name)}
        </span>
      </span>
    );
  }

  return (
    <span
      aria-hidden
      className={cn(base, "font-medium text-white/90")}
      style={{ ...box, backgroundImage: avatarBackground(seed ?? name), fontSize: size * 0.36 }}
    >
      {initials(name)}
    </span>
  );
}
